"use client";

import clsx from "clsx";
import { useTranslations } from "@/lib/compat/i18n";
import { useMoneyFormatter, useNumberFormatter } from "@/lib/i18n/format";

/**
 * Seller Trust Profile panel — used on Aurora's public catalogue page (0.5)
 * and reused for Gulf Phosphate's profile in T3. Localised via next-intl;
 * monetary + numeric values formatted via locale-aware formatters.
 */
export type SellerStats = {
  verifiedTrades: number;
  lifetimeVolumeUsd: number;
  onTimePct: number;
  disputeCount: number;
  coldChainIntegrityPct?: number;
  primarySubCorridor: string;
};

export function SellerTrustProfile({ stats, className }: { stats: SellerStats; className?: string }) {
  const t      = useTranslations("sellerTrustProfile");
  const fmtUsd = useMoneyFormatter();
  const fmtNum = useNumberFormatter();
  return (
    <div className={clsx("glass p-4", className)}>
      <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("title")}</p>
      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3">
        <Stat label={t("verifiedTrades")} value={fmtNum(stats.verifiedTrades)} />
        <Stat label={t("lifetimeVolume")} value={fmtUsd(stats.lifetimeVolumeUsd)} />
        <Stat label={t("onTime")}         value={`${stats.onTimePct}%`} />
        <Stat label={t("disputes")}       value={fmtNum(stats.disputeCount)} />
        {stats.coldChainIntegrityPct !== undefined && (
          <Stat label={t("coldChain")} value={`${stats.coldChainIntegrityPct}%`} />
        )}
        <Stat label={t("primaryCorridor")} value={stats.primarySubCorridor} />
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{label}</span>
      <span className="mt-0.5 font-display text-base font-semibold text-orbitra-paper">{value}</span>
    </div>
  );
}
